import type { components } from "@repo/api-types/merchant"
import type * as Product from "./types/product"
import type * as Table from "./types/table"
import type { SensitiveApprovalRequestConfig } from "@/utils/service"
import { request } from "@/utils/service"
import {
  refundPrepaymentBeforeTableOperation,
  refundPrepaymentsBeforeTableOperation,
} from "./orderPrepayment"

const PREFIX = "teashop_merchant/"

export type OrderCartScope = components["schemas"]["OrderCartScopeResponse"]["scope"]
export type OrderCartScopeResponse = components["schemas"]["OrderCartScopeResponse"]
export type OrderCartScopeUpdateRequest = components["schemas"]["OrderCartScopeUpdateRequest"]
export type TableCartSnapshot = Omit<components["schemas"]["MerchantTableCartSnapshotResponse"], "items" | "tableRecordId"> & {
  tableRecordId: string | number
  items: Product.MerchantTableProductVO[]
}
export type TableCartUpdateRequest = Omit<components["schemas"]["MerchantTableCartUpdateRequest"], "items" | "tableRecordId"> & {
  tableRecordId: string | number
  items: Product.MerchantTableProductVO[]
}
export type TableBillingCurrentRuleUpdateRequest = components["schemas"]["TableBillingCurrentRuleUpdateRequest"]
export type TableMaintenanceStartRequest = components["schemas"]["MerchantTableMaintenanceStartRequest"]

/**
 * 获取商户下的桌台列表
 *
 * @param shopId 商户id
 * @return 桌台列表
 */
export function getTablesByShopId(shopId: number) {
  return request<IApiResponseData<Table.MerchantTable[]>>({
    url: `${PREFIX}merchant/table/list_by_shop_id/${shopId}`,
    method: "GET",
  })
}

/** 获取类目下的桌台列表 */
export function getTableList(tableCategoryId: number) {
  return request<Table.TableListData>({
    url: `${PREFIX}merchant/table/category/tableList/${tableCategoryId}`,
    method: "GET",
  })
}

/** 上下移动桌台类目位置 */
export function moveTableCategory(data: Table.TableMoveCategoryType[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/category/move`,
    method: "POST",
    data,
  })
}

/** 上下移动桌台位置 */
export function moveTableOrder(data: Table.TableMoveType[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/move`,
    method: "POST",
    data,
  })
}

/** 新增桌台 */
export function createTable(data: Table.AddTableType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/batch`,
    method: "POST",
    data,
  })
}

/** 更新桌台 */
export function updateTable(data: Table.UpdateTableType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table`,
    method: "PUT",
    data,
  })
}

/** 获取桌台信息 */
export function getTableInfo(tableId: number) {
  return request<IApiResponseData<Table.TableItemType>>({
    url: `${PREFIX}merchant/table/${tableId}`,
    method: "GET",
  })
}

/** 获取单个营业桌台信息V2 */
export function getOpeningTableInfoV2(tableId: number) {
  return request<IApiResponseData<Table.MerchantOpeningTableVO>>({
    url: `${PREFIX}merchant/table/opening_info/v2/${tableId}`,
    method: "GET",
  })
}

/** 获取单个营业桌台信息V2 */
export function getOpeningTableInfoByRecord(recordId: number) {
  return request<IApiResponseData<Table.MerchantOpeningTableVO>>({
    url: `${PREFIX}merchant/table/opening_info_by_record/v2/${recordId}`,
    method: "GET",
  })
}

/** 辅助接口 - 获取开台相关的消费明细信息 */
export function getOpeningTableComsumeDetailByRecord(tableRecordId: string | number) {
  return request<IApiResponseData<Record<string, Table.TableItemConsumeInfo>>>({
    url: `${PREFIX}merchant/table/opening/consume/detail`,
    method: "GET",
    params: {
      tableRecordId,
    },
  })
}

/**
 * 辅助接口 - 获取当前商户的所有消费明细信息
 * merchant/table/opening/consume/total
 */
export function getOpeningTableConsumeDetailListByShopId() {
  return request<IApiResponseData<Table.TableItemConsumeInfoTuple[]>>({
    url: `${PREFIX}merchant/table/opening/consume/total`,
    method: "GET",
  })
}

/**
 * 辅助接口 - 获取当前商户的所有消费明细信息
 * merchant/table/opening/consume/total/v2
 */
export function getOpeningTableConsumeDetailListByShopIdV2() {
  return request<IApiResponseData<Table.TableItemConsumeInfoV2[]>>({
    url: `${PREFIX}merchant/table/opening/consume/total/v2`,
    method: "GET",
  })
}

/** 删除桌台 */
export function deleteTable(tableId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/${tableId}`,
    method: "DELETE",
  })
}

/**
 * 获取桌台类目列表
 * merchant/table/category/${shopId}
 */
export function getTableCategoryListByShopId(shopId: number) {
  return request<Table.TableCategoryListData>({
    url: `${PREFIX}merchant/table/category/${shopId}`,
    method: "GET",
  })
}

/**
 * 获取桌台及桌台分类信息
 * merchant/table/table_category
 */
export function getTableCategoryListV2() {
  return request<Table.TableCategoryDTOListData>({
    url: `${PREFIX}merchant/table/table_category`,
    method: "GET",
  })
}

/**
 * 获取桌台预订信息
 * merchant/table/opening/reservation
 */
export function getTableOpeningReservation() {
  return request<Table.TableOpeningReservationData>({
    url: `${PREFIX}merchant/table/opening/reservation`,
    method: "GET",
  })
}

/** 创建桌台类目 */
export function createTableCategory(data: Table.MerchantTableCategoryReqDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/category`,
    method: "POST",
    data,
  })
}

/** 更新桌台类目 */
export function updateTableCategory(data: Table.MerchantTableCategoryReqDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/category`,
    method: "PUT",
    data,
  })
}

/** 删除桌台类目 */
export function deleteTableCategory(tableCategoryId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/category/${tableCategoryId}`,
    method: "DELETE",
  })
}

/**
 * 获取营业桌台列表
 * merchant/table/opening/v2
 */
export function getOpeningTableListByShopId() {
  return request<Table.OpeningTableListData>({
    url: `${PREFIX}merchant/table/opening/v2`,
    method: "GET",
  })
}

/** 获取空闲桌台 */
export function getFreeTableListByShopId(shopId: number) {
  return request<Table.TableListData>({
    url: `${PREFIX}merchant/table/free/${shopId}`,
    method: "GET",
  })
}

/** 桌台开台 */
export function openTable(openTableType: string, reserveId: number | undefined, data: Table.MerchantTableRecord, clientRequestTime?: string) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/record/openTable`,
    method: "POST",
    params: {
      ...(clientRequestTime && { clientRequestTime }),
      openTableType,
      reserveId,
    },
    data,
  })
}

/** 桌台关台 */
export function closeTable(tableRecordId: number, closeTableType: number) {
  return request({
    url: `${PREFIX}merchant/table/record/closeTable`,
    method: "POST",
    params: {
      tableRecordId,
      closeTableType,
    },
  })
}

/** 桌台维护 */
export function maintainTable(data: TableMaintenanceStartRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/maintenance`,
    method: "POST",
    data,
    skipBusinessCodeCheck: true,
    skipGlobalErrorToast: true,
  })
}

/** 获取桌台维护记录 */
export function getTableMaintenanceInfoByTableId(tableId: number) {
  return request<IApiResponseData<Table.TableMaintenanceType>>({
    url: `${PREFIX}merchant/table/maintenance/${tableId}`,
    method: "GET",
    skipBusinessCodeCheck: true,
    skipGlobalErrorToast: true,
  })
}

/** 完成桌台维护 */
export function finishTableMaintenance(tableMaintenanceId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/maintenance/${tableMaintenanceId}/finish`,
    method: "POST",
    skipBusinessCodeCheck: true,
    skipGlobalErrorToast: true,
  })
}

/** 桌台转台 */
export async function moveTable(tableRecordId: number, tableId: number, tableBillingRuleId: number) {
  await refundPrepaymentBeforeTableOperation(tableRecordId, "TRANSFER")
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/record/moving`,
    method: "POST",
    data: {
      tableRecordId,
      tableId,
      tableBillingRuleId,
    },
  })
}

/** 桌台转台V2 */
export async function moveTableV2(params: Table.MoveTableParams) {
  await refundPrepaymentsBeforeTableOperation([
    Number(params.tableRecordId),
    Number(params.targetTableRecordId),
  ], "TRANSFER")
  const hasExistingTargetRecord = Boolean(params.targetTableRecordId)
  return request<IApiResponseData<boolean>>({
    url: hasExistingTargetRecord
      ? `${PREFIX}merchant/table/moving`
      : `${PREFIX}merchant/table/record/moving`,
    method: "POST",
    data: params,
  })
}

/** 整桌转桌批次回退 */
export function rollbackMoveTable(params: Table.MoveTableRollbackRequest) {
  return request<IApiResponseData<Table.MoveTableRollbackResponse>>({
    url: `${PREFIX}merchant/table/record/moving/rollback`,
    method: "POST",
    data: params,
  })
}

/** 修改转台后的计费方式 */
export function updateMoveTableBillingType(params: Table.MoveTableBillingTypeUpdateRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/record/moving/billing-type`,
    method: "POST",
    data: params,
  })
}

/** 修改转台后的计费方式预览 */
export function previewMoveTableBillingTypeUpdate(params: Table.MoveTableBillingTypeUpdateRequest) {
  return request<IApiResponseData<Table.MoveTableBillingPreviewResponse>>({
    url: `${PREFIX}merchant/table/record/moving/billing-type/preview`,
    method: "POST",
    data: params,
    skipBusinessCodeCheck: true,
  })
}

/** 桌台转台计费预览 */
export function previewMoveTableBilling(params: Table.MoveTableBillingPreviewParams) {
  return request<IApiResponseData<Table.MoveTableBillingPreviewResponse>>({
    url: `${PREFIX}merchant/table/record/moving/preview`,
    method: "POST",
    params,
    skipBusinessCodeCheck: true,
  })
}

/** 桌台暂停 */
export function pauseTable(tableRecordId: number, pauseMinutes: number) {
  return request({
    url: `${PREFIX}merchant/table/record/pause`,
    method: "POST",
    params: {
      tableRecordId,
      pauseMinutes,
    },
  })
}

/** 新增桌台预订 */
export function createTableReservation(data: Table.TableReservationVO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/oms/table/reservation/batch_add`,
    method: "POST",
    data,
  })
}

/** 修改C端桌台预订 */ // teashop_merchant/merchant_oms_customer_reservation/update_table_reservation
export function updateCustomerTableReservation(data: object) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_customer_reservation/update_table_reservation`,
    method: "POST",
    data,
  })
}
/** 修改桌台预订 */
export function updateTableReservation(data: Table.MerchantOmsTableReservation) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/oms/table/reservation`,
    method: "PUT",
    data,
  })
}

/** 取消桌台预订 */
export function cancelTableReservation(tableReservationId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/oms/table/reservation/${tableReservationId}`,
    method: "POST",
  })
}

/** 获取某商户未来7日桌台预订列表 */
export function getTableReservationList(shopId: number, startTime: Date) {
  return request<Table.TableReservationWithDateListData>({
    url: `${PREFIX}merchant/oms/table/reservation/seven`,
    method: "POST",
    data: {
      shopId,
      startTime,
    },
  })
}

/** 获取单个桌台近期预订列表 */
export function getTableReservationListByTableId(tableId: number, options?: { silent?: boolean }) {
  return request<Table.TableReservationListData>({
    url: `${PREFIX}merchant/oms/table/reservation/list_by_table_id/${tableId}`,
    method: "GET",
    ...(options?.silent
      ? {
          skipBusinessCodeCheck: true,
          skipGlobalErrorToast: true,
        }
      : {}),
  })
}

/** 获取可预订桌台列表 */
export function getAvailableTableList(shopId: number) {
  return request<Table.TableListData>({
    url: `${PREFIX}merchant/table/available/${shopId}`,
    method: "GET",
  })
}

/** 查询当前时间前后4小时是否有预订 */
export function getCurrentReservation(tableId: number, startTime: Date, reserveId: number) {
  console.log(tableId, startTime, reserveId)

  return request<Table.TableReservationListData>({
    url: `${PREFIX}merchant/oms/table/reservation/current`,
    method: "POST",
    data: {
      tableId,
      startTime,
      reserveId,
    },
  })
}

/** 获取桌台获取计费规则列表 */
export function getTableBillingRuleListByTableId(tableId: string | number) {
  return request<Table.TableBillingRuleListData>({
    url: `${PREFIX}merchant/table/billingRule/list_by_table_id/${tableId}`,
    method: "GET",
  })
}

/** 修改桌台订单 */
export function updateTableRecord(data: Table.MerchantTableRecord) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/record/update`,
    method: "POST",
    data,
  })
}

type CurrentBillingRuleRequestError = {
  response?: {
    status?: unknown
    data?: unknown
  }
}

const isCurrentBillingRuleBusinessEnvelope = (value: unknown): value is IApiResponseData<void> => Boolean(
  value
  && typeof value === "object"
  && !Array.isArray(value)
  && typeof (value as { code?: unknown }).code === "number"
  && Number.isFinite((value as { code: number }).code)
  && (value as { code: number }).code !== 0,
)

/** 修改服务端权威当前计费规则。调用方负责区分成功、业务失败和未知结果。 */
export async function updateCurrentTableBillingRule(data: TableBillingCurrentRuleUpdateRequest) {
  try {
    return await request<IApiResponseData<void>>({
      url: `${PREFIX}merchant/table/billing-segments/current-rule`,
      method: "PATCH",
      data,
      skipBusinessCodeCheck: true,
      skipGlobalErrorToast: true,
    })
  } catch (error) {
    const response = (error as CurrentBillingRuleRequestError | null)?.response
    const status = response?.status
    if (typeof status === "number" && status >= 400 && status < 500
      && isCurrentBillingRuleBusinessEnvelope(response?.data)) {
      return response.data
    }
    throw error
  }
}

/** 取消桌台订单 */
export async function calcelTableRecord(tableRecordId: number, cancelReason: string, options?: SensitiveApprovalRequestConfig) {
  await refundPrepaymentBeforeTableOperation(tableRecordId, "ORDER_CANCEL")
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/record/cancel/${tableRecordId}`,
    method: "POST",
    params: {
      cancelReason,
    },
    sensitiveApprovalBusinessName: options?.sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows: options?.sensitiveApprovalDetailRows,
    sensitiveApprovalReason: options?.sensitiveApprovalReason,
  })
}

/** 获取正在使用中的桌台 */
export function getUsingTableListByShopId(shopId: number) {
  return request<Table.TableListData>({
    url: `${PREFIX}merchant/table/using/${shopId}`,
    method: "GET",
  })
}

/** 桌台结账 */
export function tableSettlement(tableId: number | string) {
  return request<IApiResponseData<number>>({
    url: `${PREFIX}merchant/table/settlement/v2/${tableId}`,
    method: "POST",
  })
}

/** 预览桌台最新费用段调为新计费 */
export function previewAdjustTableBillingSegment(data: Table.TableBillingSegmentAdjustPreviewRequest) {
  return request<IApiResponseData<Table.TableBillingSegmentPreviewResponse>>({
    url: `${PREFIX}merchant/table/billing-segments/adjust/preview`,
    method: "POST",
    data,
  })
}

/** 确认桌台最新费用段调为新计费 */
export function confirmAdjustTableBillingSegment(data: Table.TableBillingSegmentAdjustConfirmRequest) {
  return request<IApiResponseData<Table.TableBillingSegmentOperationResponse>>({
    url: `${PREFIX}merchant/table/billing-segments/adjust/confirm`,
    method: "POST",
    data,
  })
}

/** 预览撤回最新手动费用段 */
export function previewRollbackTableBillingSegment(data: Table.TableBillingSegmentRollbackPreviewRequest) {
  return request<IApiResponseData<Table.TableBillingSegmentPreviewResponse>>({
    url: `${PREFIX}merchant/table/billing-segments/rollback/preview`,
    method: "POST",
    data,
  })
}

/** 确认撤回最新手动费用段 */
export function confirmRollbackTableBillingSegment(data: Table.TableBillingSegmentRollbackConfirmRequest) {
  return request<IApiResponseData<Table.TableBillingSegmentOperationResponse>>({
    url: `${PREFIX}merchant/table/billing-segments/rollback/confirm`,
    method: "POST",
    data,
  })
}

/** 管理员重试修复可用时间外自动转计费异常 */
export function repairTableBillingAutoSwitch(data: Table.TableBillingSegmentRepairRequest) {
  return request<IApiResponseData<Table.TableBillingSegmentOperationResponse>>({
    url: `${PREFIX}merchant/table/billing-segments/auto-switch/repair`,
    method: "POST",
    data,
  })
}

/** 获取所有桌台规则列表 */
export function getTableBillingRuleListByShopId(shopId: number) {
  return request<IApiResponseData<Table.MerchantTableBillingRule[]>>({
    url: `${PREFIX}merchant/table/billingRule/${shopId}`,
    method: "GET",
  })
}

/** 获取计费规则管理分类列表 */
export function getBillingRuleCategoryList(shopId: number) {
  return request<IApiResponseData<Table.BillingRuleCategoryResponse[]>>({
    url: `${PREFIX}merchant/table/billingRule/category/${shopId}`,
    method: "GET",
  })
}

/** 新增计费规则管理分类 */
export function createBillingRuleCategory(data: Table.CreateBillingRuleCategoryRequest) {
  return request<IApiResponseData<Table.BillingRuleCategoryResponse>>({
    url: `${PREFIX}merchant/table/billingRule/category`,
    method: "POST",
    data,
  })
}

/** 修改计费规则管理分类 */
export function updateBillingRuleCategory(data: Table.UpdateBillingRuleCategoryRequest) {
  return request<IApiResponseData<Table.BillingRuleCategoryResponse>>({
    url: `${PREFIX}merchant/table/billingRule/category`,
    method: "PUT",
    data,
  })
}

/** 删除计费规则管理分类 */
export function deleteBillingRuleCategory(billingRuleCategoryId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/billingRule/category/${billingRuleCategoryId}`,
    method: "DELETE",
  })
}

/** 获取对应桌台类目的规则列表 */
export function getTableBillingRuleRelListByCategoryId(tableCategoryId: number) {
  return request<IApiResponseData<Table.TableBillingRuleRelType[]>>({
    url: `${PREFIX}merchant_table_category_billing_rule_rel/ruleList/${tableCategoryId}`,
    method: "GET",
  })
}

/** 获取对应规则的桌台类目列表 */
export function getCategoryListByRuleId(tableBillingRuleId: number) {
  return request<IApiResponseData<Table.MerchantTableCategoryReqDTO[]>>({
    url: `${PREFIX}merchant_table_category_billing_rule_rel/categoryList/${tableBillingRuleId}`,
    method: "GET",
  })
}

/** 新增桌台规则 */
export function createTableBillingRule(data: Table.MerchantTableBillingRule) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/billingRule`,
    method: "POST",
    data,
  })
}

/** 更新桌台规则 */
export function updateTableBillingRule(data: Table.MerchantTableBillingRule) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/billingRule`,
    method: "PUT",
    data,
  })
}

/** 删除桌台规则 */
export function deleteTableBillingRule(tableBillingRuleId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/billingRule/${tableBillingRuleId}`,
    method: "DELETE",
  })
}

/** 获取桌台规则修改记录列表 */
export function getTableBillingRuleUpdateListByRuleId(tableBillingRuleId: number) {
  return request<IApiResponseData<Table.TableBillingRuleUpdateRecordType[]>>({
    url: `${PREFIX}merchant_table_billing_rule_amend_record/list/${tableBillingRuleId}`,
    method: "GET",
  })
}

/** 修改规则绑定的类目列表 */
export function updateCategoryList(tableBillingRuleId: number, categoryIds: number[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_table_category_billing_rule_rel/categoryList/${tableBillingRuleId}`,
    method: "PUT",
    data: categoryIds,
  })
}

/** 通过tableId查询二维码,如果没有则生成 */
export function getTableQRCodeByTableId(tableId: number) {
  return request<IApiResponseData<Table.QRCodeInfoType>>({
    url: `${PREFIX}merchant_table_qrcode/get_by_table_id/${tableId}`,
    method: "GET",
  })
}

/** 获取桌台购物车商品数据 */
export function getTableCartProductList(tableId: number, source: string) {
  return request<IApiResponseData<Product.MerchantTableProductVO[]>>({
    url: `${PREFIX}merchant/table_cart/get/${tableId}/${source}`,
    method: "GET",
  })
}

/** 设置桌台购物车商品数据 */
export function setTableCartProductList(tableId: number, source: string, data: Product.MerchantTableProductVO[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table_cart/set/${tableId}/${source}`,
    method: "POST",
    data,
  })
}

/** 获取当前员工在桌台会话下的 B 端购物车快照 */
export function getTableCartSnapshot(tableId: number, tableRecordId: string | number) {
  return request<IApiResponseData<TableCartSnapshot>>({
    url: `${PREFIX}merchant/table_cart/v2/${tableId}`,
    method: "GET",
    params: { tableRecordId },
  })
}

/** 覆盖当前员工在桌台会话下的 B 端购物车快照 */
export function setTableCartSnapshot(tableId: number, data: TableCartUpdateRequest) {
  return request<IApiResponseData<TableCartSnapshot>>({
    url: `${PREFIX}merchant/table_cart/v2/${tableId}`,
    method: "PUT",
    data,
  })
}

/** 查询门店 B 端购物车隔离范围 */
export function getOrderCartScope() {
  return request<IApiResponseData<OrderCartScopeResponse>>({
    url: `${PREFIX}merchant_shop/order_cart_scope`,
    method: "GET",
  })
}

/** 更新门店 B 端购物车隔离范围 */
export function updateOrderCartScope(data: OrderCartScopeUpdateRequest) {
  return request<IApiResponseData<OrderCartScopeResponse>>({
    url: `${PREFIX}merchant_shop/order_cart_scope`,
    method: "PATCH",
    data,
  })
}

/** 计算桌台费用 */
export function computedTimeFeeByDate(data: Table.MerchantTableRecord) {
  return request<IApiResponseData<number>>({
    url: `${PREFIX}merchant/table/computed_fee/v2`,
    method: "POST",
    data,
  })
}

/** 增加桌台暂停 */
export function addTablePauseRecord(data: Table.MerchantTablePauseRecord) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_table_pause_record`,
    method: "POST",
    data,
  })
}

/** 获取桌台暂停记录 */
export function getTablePauseRecordListByTableRecordId(tableRecordId: number) {
  return request<IApiResponseData<Table.MerchantTablePauseRecord[]>>({
    url: `${PREFIX}merchant_table_pause_record/table_record_id/${tableRecordId}`,
    method: "GET",
  })
}

/** 延时桌台暂停 */
export function updateTablePauseRecord(data: Table.MerchantTablePauseRecord) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_table_pause_record`,
    method: "PUT",
    data,
  })
}

/** 恢复计时 */
export function recoverTablePauseRecord(tableRecordId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_table_pause_record/resume`,
    method: "POST",
    params: {
      tableRecordId,
    },
  })
}

/** 计时作废 */
export function invalidTablePauseRecord(merchantTablePauseRecordId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_table_pause_record/cancel`,
    method: "POST",
    params: {
      merchantTablePauseRecordId,
    },
  })
}

/** 修改预订状态 */
export function updateReservable(reservable: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop/update_reservable`,
    method: "POST",
    params: {
      reservable,
    },
  })
}

/** 修改预订设置 */
export function updateReservableSetting(data: Table.ReserveSettingVOType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop/update_reservable_setting`,
    method: "POST",
    data,
  })
}

/** 获取预订设置 */
export function getReservableSetting() {
  return request<IApiResponseData<Table.ReserveSettingVOType>>({
    url: `${PREFIX}merchant_shop/get_reservable_setting`,
    method: "GET",
  })
}

/** 获取店铺所有权限 */
export function getShopPermissionList(shopId: number) {
  return request<IApiResponseData<string[]>>({
    url: `${PREFIX}merchant_shop/get_shop_permission`,
    params: {
      shopId,
    },
    method: "GET",
  })
}

/** 获取店铺所有权限(自己的店铺) */
export function getShopPermissionSelf() {
  return request<IApiResponseData<string[]>>({
    url: `${PREFIX}merchant_shop/get_shop_permission_self`,
    method: "GET",
  })
}

/** 获取店铺系统续费相关信息 */
export function getRenewInfo() {
  return request<IApiResponseData<Table.MerchantShopRenewInfoVO[]>>({
    url: `${PREFIX}merchant_shop/get_renew_info`,
    method: "GET",
  })
}

export function getIotProviderOptions() {
  return request<IApiResponseData<Table.IotProviderAccount[]>>({
    url: `${PREFIX}merchant/iot/provider-options`,
    method: "GET",
  })
}

export function getIotProviderDevices(accountId: number) {
  return request<IApiResponseData<Table.IotProviderDeviceListResponse>>({
    url: `${PREFIX}merchant/iot/provider-options/${accountId}/devices`,
    method: "GET",
  })
}

export function testIotProviderConnection(accountId: number, data: Table.IotProviderConnectionTestRequest) {
  return request<IApiResponseData<Table.IotProviderConnectionTestResponse>>({
    url: `${PREFIX}merchant/iot/provider-options/${accountId}/connection-tests`,
    method: "POST",
    data,
  })
}

export function getTableDeviceBindings(shopId: number) {
  return request<IApiResponseData<Table.TableDeviceBinding[]>>({
    url: `${PREFIX}merchant/table/device-bindings`,
    method: "GET",
    params: { shopId },
  })
}

export function saveTableDeviceBinding(data: Table.TableDeviceBindingSaveRequest) {
  return request<IApiResponseData<Table.TableDeviceBinding>>({
    url: `${PREFIX}merchant/table/device-bindings`,
    method: "PUT",
    data,
  })
}

export function disableTableDeviceBinding(bindingId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/device-bindings/${bindingId}`,
    method: "DELETE",
  })
}

export function getTableVoiceDeviceBindings(shopId: number) {
  return request<IApiResponseData<Table.TableVoiceDeviceBinding[]>>({
    url: `${PREFIX}merchant/table/voice-device-bindings`,
    method: "GET",
    params: { shopId },
  })
}

export function saveTableVoiceDeviceBinding(data: Table.TableVoiceDeviceBindingSaveRequest) {
  return request<IApiResponseData<Table.TableVoiceDeviceBinding>>({
    url: `${PREFIX}merchant/table/voice-device-bindings`,
    method: "PUT",
    data,
  })
}

export function disableTableVoiceDeviceBinding(bindingId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/voice-device-bindings/${bindingId}`,
    method: "DELETE",
  })
}

export function testTableVoiceAnnouncement(
  bindingId: number,
  data: Table.TableVoiceAnnouncementTestRequest,
) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/voice-device-bindings/${bindingId}/test`,
    method: "POST",
    data,
  })
}

export function getTableDeviceControlStatuses(shopId: number) {
  return request<IApiResponseData<Table.TableDeviceControlStatus[]>>({
    url: `${PREFIX}merchant/table/device-control/statuses`,
    method: "GET",
    params: { shopId },
  })
}

export function getTableDeviceControlStatus(tableId: number) {
  return request<IApiResponseData<Table.TableDeviceControlStatus>>({
    url: `${PREFIX}merchant/table/${tableId}/device-control/status`,
    method: "GET",
    skipBusinessCodeCheck: true,
    skipGlobalErrorToast: true,
  })
}

export function getTableDeviceCommandStatus(commandId: number) {
  return request<IApiResponseData<Table.TableDeviceCommandStatus>>({
    url: `${PREFIX}merchant/table/device-control/commands/${commandId}`,
    method: "GET",
  })
}

export function openTableDevice(tableId: number) {
  return request<IApiResponseData<Table.TableDeviceControlResult>>({
    url: `${PREFIX}merchant/table/${tableId}/device-control/open`,
    method: "POST",
  })
}

export function closeTableDevice(tableId: number) {
  return request<IApiResponseData<Table.TableDeviceControlResult>>({
    url: `${PREFIX}merchant/table/${tableId}/device-control/close`,
    method: "POST",
  })
}
